(ns renolit-roofing-service.util.google-maps
  (:require ["@vis.gl/react-google-maps" :refer [useApiIsLoaded useMap useMapsLibrary
                                                 APIProvider AdvancedMarker
                                                 ControlPosition Map MapControl]]
            ["react" :refer [useEffect useState useRef]]
            [clojure.set :as set]
            [medley.core :as medley]
            [re-frame.core :as rf]
            [renolit-roofing-service.layout.icon :as layout.icon]
            [renolit-roofing-service.localization :refer [t]]
            [renolit-roofing-service.util.geometry :as util.geometry]))

(defn gmap-width
  [gmap]
  (let [bounds (.getBounds gmap)
        left (.lng (.getSouthWest bounds))
        right (.lng (.getNorthEast bounds))]
    (- right left)))

(defn gmap-height
  [gmap]
  (let [bounds (.getBounds gmap)
        top (.lat (.getNorthEast bounds))
        bottom (.lat (.getSouthWest bounds))]
    (- top bottom)))

(defn fit-bounds
  [^google.maps.Map gmap bounds]
  (doto gmap
    (.fitBounds (clj->js bounds))))

(defn x-y->lng-lat
  [gmap {:keys [x y]}]
  (let [div-bounds (.getBoundingClientRect ^HTMLElement (.getDiv gmap))
        map-bounds (.getBounds gmap)
        map-left (.lng ^google.maps.LatLng (.getSouthWest map-bounds))
        map-right (.lng ^google.maps.LatLng (.getNorthEast map-bounds))
        map-top (.lat ^google.maps.LatLng (.getNorthEast map-bounds))
        map-bottom (.lat ^google.maps.LatLng (.getSouthWest map-bounds))
        map-width (- map-right map-left)
        map-height (- map-top map-bottom)
        relative-x (double (/ (- x (.-left div-bounds)) (.-width div-bounds)))
        relative-y (double (/ (- y (.-top div-bounds)) (.-height div-bounds)))]
    [(+ map-left (* relative-x map-width))
     (+ map-bottom (* (- 1 relative-y) map-height))]))

(defn marker-position
  [marker]
  ((juxt #(.-lng %) #(.-lat %)) (.-position marker)))

(defn marker-map
  [marker]
  (.-map marker))

(defn polygon-path
  [^js polygon]
  (->> (.getArray (.getPath polygon))
       (array-seq)
       (mapv (fn [^js ll] [(.lng ll) (.lat ll)]))))

(defn set-polygon-options
  [polygon options]
  (doto ^google.maps.Polygon polygon
    (.setOptions (clj->js options))))

(defn delete-polygon
  [polygon]
  (when-let [dash (aget polygon "__dashPolyline")]
    (.setMap dash nil))
  (doto ^google.maps.Polygon polygon
    (.setMap nil)))

(defn polygon'
  [gmaps options]
  (gmaps.Polygon.
   (clj->js
    (-> options
        (update :paths (fn [path]
                         (->> path
                              (mapv (fn [[lng lat]] {:lat lat, :lng lng})))))))))

(defn pin
  [gmaps-marker options]
  (gmaps-marker.PinElement. (clj->js options)))

(defn marker'
  [gmaps-marker options]
  (gmaps-marker.AdvancedMarkerElement.
   (clj->js
    (-> options
        (update :position (fn [[lng lat]] {:lat lat, :lng lng}))))))

(defn delete-marker
  [marker]
  (set! (.-map marker) nil)
  marker)

(defn on!
  [object event handler]
  (doto object
    (.addListener event handler)))

(defn on-path!
  [polygon event handler]
  (doto (.getPath polygon)
    (.addListener event handler))
  polygon)

(defn- on-polygon-edit!
  [^js poly handler]
  (let [listen! (fn [^js obj event handler]
                  (.addListener obj event handler))]
    (let [path (.getPath poly)]
      (listen! path "insert_at" handler)
      (listen! path "remove_at" handler)
      (listen! path "set_at" handler))
    (let [paths (.getPaths poly)]
      (listen! paths "insert_at" handler)
      (listen! paths "remove_at" handler)
      (listen! paths "set_at" handler)))
  poly)

(defn- set-marker-position!
  [marker [lng lat]]
  (when (and marker (number? lng) (number? lat))
    (set! (.-position marker) #js {:lat lat :lng lng}))
  marker)

(defn- set-polygon-path!
  [^js polygon lnglat-path]
  (let [path (clj->js
              (mapv (fn [[lng lat]]
                      {:lat lat :lng lng})
                    lnglat-path))]
    (.setPath polygon path))
  polygon)

(defn- stash-old-path!
  [polygon]
  (aset polygon "__editing" true)
  (aset polygon "__oldPath" (polygon-path polygon))
  polygon)

(defn- pop-old-path
  [polygon]
  (let [old (aget polygon "__oldPath")]
    (aset polygon "__oldPath" nil)
    (aset polygon "__editing" false)
    old))

(defn gmaps-bridge!
  [_props]
  (let [reg-ref (useRef nil)
        registry-ready? (useRef false)
        gmaps-loaded? (useApiIsLoaded)
        gmaps-marker (useMapsLibrary "marker")
        gmap (useMap)]
    (when-not (.-current reg-ref)
      (set! (.-current reg-ref)
            {:gmaps (atom nil)
             :gmaps-marker (atom nil)
             :gmap (atom nil)
             :zones (atom {})
             :pois (atom {})}))
    (useEffect
     (fn []
       (when gmaps-loaded?
         (reset! (:gmaps (.-current reg-ref)) google.maps))
       js/undefined)
     #js [gmaps-loaded?])
    (useEffect
     (fn []
       (when gmaps-marker
         (reset! (:gmaps-marker (.-current reg-ref)) gmaps-marker))
       js/undefined)
     #js [gmaps-marker])
    (useEffect
     (fn []
       (when gmap
         (reset! (:gmap (.-current reg-ref)) gmap))
       js/undefined)
     #js [gmap])
    (useEffect
     (fn []
       (when (and (not (.-current registry-ready?))
                  gmaps-loaded?
                  gmap)
         (set! (.-current registry-ready?) true)
         (rf/dispatch [::bridge-registry-ready (.-current reg-ref)]))
       js/undefined)
     #js [gmaps-loaded? gmap])
    (useEffect
     (fn []
       (when (and (.-current registry-ready?)
                  gmaps-marker)
         (rf/dispatch [::render-overlays]))
       js/undefined)
     #js [gmaps-marker])
    nil))

(defn zones->tree
  [nodes]
  (let [root-zone-ids
        (reduce-kv
         (fn [acc id n]
           (if (and (:zone/id n) (nil? (:zone/parent-zone-id n)))
             (conj acc id)
             acc))
         []
         nodes)
        build-zone
        (fn build-zone [zid]
          (let [z (get nodes zid)]
            (when (:zone/id z)
              (let [subzone-ids (or (:zone/subzones z) #{})
                    poi-ids (or (:zone/pois z) #{})
                    subzones (reduce
                              (fn [acc cid]
                                (if-let [child (build-zone cid)]
                                  (conj acc child)
                                  acc))
                              []
                              subzone-ids)
                    pois (reduce
                          (fn [acc pid]
                            (let [p (get nodes pid)]
                              (if (:poi/id p)
                                (conj acc p)
                                acc)))
                          []
                          poi-ids)]
                (assoc z
                       :zone/subzones subzones
                       :zone/pois pois)))))]
    (reduce
     (fn [acc rid]
       (if-let [root (build-zone rid)]
         (conj acc root)
         acc))
     []
     root-zone-ids)))

(defn- descendant-zone-ids
  [nodes root-id]
  (loop [seen #{}
         stack [root-id]]
    (if (empty? stack)
      seen
      (let [zid (peek stack)
            stack (pop stack)]
        (if (contains? seen zid)
          (recur seen stack)
          (let [subzs (or (get-in nodes [zid :zone/subzones]) #{})]
            (recur (conj seen zid) (into stack subzs))))))))

(defn- descendant-poi-ids
  [nodes root-zone-id]
  (let [zids (descendant-zone-ids nodes root-zone-id)]
    (reduce
     (fn [acc zid]
       (into acc (or (get-in nodes [zid :zone/pois]) #{})))
     #{}
     zids)))

(defn- zone-area-rect
  [poly]
  (let [lngs (map first poly)
        lats (map second poly)
        w (- (apply max lngs) (apply min lngs))
        h (- (apply max lats) (apply min lats))]
    (* w h)))

(defn- mark-editing!
  [polygon]
  (aset polygon "__editing" true)
  polygon)

(defn- containing-zone-id
  [nodes p]
  (let [pt (util.geometry/point p)]
    (->> nodes
         (keep (fn [[zid n]]
                 (when-let [poly (:zone/polygon n)]
                   (when (util.geometry/point-in-polygon?
                          pt
                          (util.geometry/polygon poly))
                     {:id zid :area (zone-area-rect poly)}))))
         (sort-by :area)
         first
         :id)))

(defn- root-parent-id
  [nodes candidate]
  (when (get-in nodes [candidate :zone/id])
    (or (get-in nodes [candidate :zone/parent-zone-id])
        candidate)))

(rf/reg-event-db
 ::open-gmaps-options-menu
 (fn [db [_ pos]]
   (assoc db :gmaps/options-menu pos)))

(rf/reg-event-db
 ::close-gmaps-options-menu
 (fn [db _]
   (dissoc db :gmaps/options-menu)))

(rf/reg-sub
 ::gmaps-options-menu
 (fn [db _]
   (:gmaps/options-menu db)))

(rf/reg-sub
 ::zones
 (fn [db] (:zones db)))

(rf/reg-sub
 ::zones-tree
 (fn [db] (zones->tree (:zones db))))

(rf/reg-event-fx
 ::bridge-registry-ready
 (fn [{:keys [db]} [_ reg]]
   {:db (assoc db :gmaps/bridge-registry reg)
    :fx [[:dispatch [::render-overlays]]]}))

(rf/reg-event-fx
 ::zone-clicked
 (fn [{:keys [db]} [_ {:keys [zone-id coords context-position]}]]
   (if (= (:gmaps/placing-tool db) :zone)
     {:db (dissoc db :gmaps/placing-tool)
      :fx [[:dispatch [::zone-created {:center coords
                                       :parent-zone-id zone-id}]]]}
     (when context-position
       {:fx [[:dispatch [::open-gmaps-options-menu
                         {:context-position context-position
                          :context-coordinates coords
                          :parent-zone-id zone-id}]]]}))))

(defn- dashed-polyline
  "Creates a polyline with a dashed pattern along the zone polygon boundary."
  [gmaps gmap polygon]
  (let [closed-path (->> (conj (vec polygon) (first polygon))
                         (mapv (fn [[lng lat]] {:lat lat :lng lng})))
        dash-symbol #js {:path "M 0,-1 0,1"
                         :strokeOpacity 1
                         :strokeColor "#364969"
                         :strokeWeight 2
                         :scale 2}]
    (gmaps.Polyline.
     (clj->js {:path closed-path
               :strokeOpacity 0
               :icons [{:icon dash-symbol
                        :offset "0"
                        :repeat "12px"}]
               :map gmap}))))

(defn- render-zone
  [{:zone/keys [id polygon zIndex]} gmaps gmap read-only?]
  (let [poly (polygon'
              gmaps
              {:draggable (not read-only?)
               :editable (not read-only?)
               :fillColor "#808080"
               :fillOpacity 0.2
               :strokeColor (if read-only? "transparent" "#364969")
               :strokeWeight (if read-only? 0 2)
               :map gmap
               :paths polygon
               :zIndex (or zIndex 0)})
        dash (when read-only?
               (dashed-polyline gmaps gmap polygon))
        _ (when dash (aset poly "__dashPolyline" dash))]
    (when-not read-only?
      (let [send! (fn []
                    (mark-editing! poly)
                    (rf/dispatch [::zone-changed id (polygon-path poly)]))]
        (on! poly "click"
             (fn [original-mouse-evt]
               (let [latlng (.-latLng original-mouse-evt)
                     coordinates {:lat (.lat latlng) :lng (.lng latlng)}]
                 (rf/dispatch [::zone-changed id (polygon-path poly)])
                 (rf/dispatch
                  [::zone-clicked {:zone-id id
                                   :coords coordinates
                                   :context-position nil}]))))
        (on! poly "rightclick" (fn [original-mouse-evt]
                                 (let [latlng (.-latLng original-mouse-evt)
                                       coordinates {:lat (.lat latlng) :lng (.lng latlng)}
                                       e (.-domEvent original-mouse-evt)
                                       context-position {:x (.-clientX e)
                                                         :y (.-clientY e)}]
                                   (.stopPropagation e)
                                   (rf/dispatch [::zone-clicked {:zone-id id
                                                                 :coords coordinates
                                                                 :context-position context-position}]))))
        (on! poly "dragstart"
             (fn [_]
               (let [old (polygon-path poly)]
                 (stash-old-path! poly)
                 (rf/dispatch [::zone-drag-started {:zone-id id
                                                    :old-polygon old}]))))
        (on! poly "dragend"
             (fn [_]
               (let [old (pop-old-path poly)
                     new (polygon-path poly)]
                 (rf/dispatch-sync [::zone-drag-ended id old new]))))
        (on-polygon-edit! poly (fn [_] (send!)))))
    poly))

(def ^:const wp->image
  {:visual-inspection "/images/visual-inspection.svg"
   :non-destructive-testing "/images/non-destructive-test.svg"
   :destructive-testing "/images/destructive-test.svg"
   :legend "/images/wp-legend.svg"})

(defn wp-marker
  ([wp]
   (wp-marker wp nil))
  ([wp cls]
   [:img {:key (or (:key wp) (random-uuid))
          :height 25
          :width 25
          :alt "Focused WP"
          :class cls
          :src (get wp->image (keyword (:wp wp)))}]))

(def ^:const wp->icon-key
  {:visual-inspection :triangle
   :non-destructive-testing :circle
   :destructive-testing :square})

(defn wp-icon
  [wp]
  [layout.icon/icon {:key (get wp->icon-key (keyword (:wp wp))) :class "mr-4"}])

(defn- svg-el
  "Converts a hiccup-like vector to a native SVG DOM element.
   Usage: (svg-el [:circle {:cx 5 :cy 5 :r 3} child1 child2])"
  [[tag attrs & children]]
  (let [el (.createElementNS js/document "http://www.w3.org/2000/svg" (name tag))]
    (doseq [[k v] attrs]
      (.setAttribute el (name k) (str v)))
    (doseq [child children]
      (.appendChild el (svg-el child)))
    el))

(def ^:private status-colors
  {:pending {:bg "#DBE9FE" :stroke "#2463EB"}
   :green   {:bg "#E6FEE8" :stroke "#16A34A"}
   :red     {:bg "#FEE6E7" :stroke "#DC2626"}})

(def ^:private wp-shape-paths
  {:visual-inspection
   {:d "M5.244 6.639C6.011 5.13 6.393 4.375 7 4.375C7.607 4.375 7.99 5.13 8.756 6.639L8.851 6.827C9.488 8.081 9.806 8.708 9.518 9.167C9.231 9.625 8.519 9.625 7.095 9.625H6.905C5.481 9.625 4.769 9.625 4.482 9.167C4.194 8.708 4.512 8.081 5.149 6.827L5.244 6.639Z"
    :clip? true}
   :non-destructive-testing
   {:d "M10 7C10 7.394 9.922 7.784 9.772 8.148C9.621 8.512 9.4 8.843 9.121 9.121C8.843 9.4 8.512 9.621 8.148 9.772C7.784 9.922 7.394 10 7 10C6.606 10 6.216 9.922 5.852 9.772C5.488 9.621 5.157 9.4 4.879 9.121C4.6 8.843 4.379 8.512 4.228 8.148C4.078 7.784 4 7.394 4 7C4 6.204 4.316 5.441 4.879 4.879C5.441 4.316 6.204 4 7 4C7.796 4 8.559 4.316 9.121 4.879C9.684 5.441 10 6.204 10 7Z"
    :clip? false}
   :destructive-testing
   {:d "M4 6.133C4 5.387 4 5.013 4.145 4.728C4.273 4.477 4.477 4.273 4.728 4.145C5.013 4 5.387 4 6.133 4H7.867C8.613 4 8.987 4 9.272 4.145C9.523 4.273 9.727 4.477 9.855 4.728C10 5.013 10 5.387 10 6.133V7.867C10 8.613 10 8.987 9.855 9.272C9.727 9.523 9.523 9.727 9.272 9.855C8.987 10 8.613 10 7.867 10H6.133C5.387 10 5.013 10 4.728 9.855C4.477 9.727 4.273 9.523 4.145 9.272C4 8.987 4 8.613 4 7.867V6.133Z"
    :clip? false}})

(defn wp-marker-element
  "Creates a native SVG DOM element for a work-package marker icon,
   colored by status. Defaults to :pending (blue) when status is nil."
  ([wp] (wp-marker-element wp nil))
  ([wp status]
   (let [wp-kw (keyword wp)
         {:keys [bg stroke]} (get status-colors (or status :pending))
         {:keys [d clip?]} (get wp-shape-paths wp-kw)
         path-attrs {:d d :stroke stroke :stroke-width "0.75"
                     :stroke-linecap "round" :stroke-linejoin "round"}
         shape (when d
                 (if clip?
                   (let [clip-id (str "clip-" (random-uuid))]
                     [[:defs {}
                       [:clipPath {:id clip-id}
                        [:rect {:width 7 :height 7 :fill "white"
                                :transform "translate(3.5 3.5)"}]]]
                      [:g {:clip-path (str "url(#" clip-id ")")}
                       [:path path-attrs]]])
                   [[:path path-attrs]]))]
     (svg-el
      (into [:svg {:width 25 :height 25 :viewBox "0 0 14 14" :fill "none"}
             [:rect {:width 14 :height 14 :rx 7 :fill bg}]]
            shape)))))

(defn- render-poi
  [{:poi/keys [id marker work-package status] :as _poi} gmaps-marker gmap read-only?]
  (let [img (wp-marker-element (:wp work-package) status)
        m (marker'
           gmaps-marker
           {:content img
            :gmpClickable (not read-only?)
            :gmpDraggable (not read-only?)
            :map gmap
            :position marker})]
    (when-not read-only?
      (on! m "click" #(rf/dispatch-sync [::poi-selected id]))
      (on! m "dragend" #(rf/dispatch-sync [::poi-moved id (marker-position m)])))
    m))

(rf/reg-fx
 ::sync-overlays
 (fn [{:keys [gmaps gmaps-marker gmap zones overlays-reg read-only?]}]
   (let [zone-overlays* (:zones overlays-reg)
         poi-overlays* (:pois overlays-reg)
         desired-zone-ids (->> (vals zones) (keep :zone/id) set)
         desired-poi-ids (->> (vals zones) (keep :poi/id) set)
         existing-zone-ids (set (keys @zone-overlays*))
         existing-poi-ids (set (keys @poi-overlays*))]
     (doseq [id (set/difference existing-zone-ids desired-zone-ids)]
       (when-let [poly (get @zone-overlays* id)]
         (delete-polygon poly)
         (swap! zone-overlays* dissoc id)))
     (doseq [id (set/difference existing-poi-ids desired-poi-ids)]
       (when-let [m (get @poi-overlays* id)]
         (delete-marker m)
         (swap! poi-overlays* dissoc id)))
     (doseq [[id node] zones]
       (cond
         (:zone/id node)
         (if-let [_poly (get @zone-overlays* id)]
           nil
           (swap! zone-overlays* assoc id (render-zone node gmaps gmap read-only?)))

         (and (:poi/id node) (= (:poi/type node) :focused-revision) gmaps-marker)
         (if-let [m (get @poi-overlays* id)]
           (set-marker-position! m (:poi/marker node))
           (swap! poi-overlays* assoc id (render-poi node gmaps-marker gmap read-only?))))))))

(rf/reg-event-fx
 ::render-overlays
 (fn [{:keys [db]} _]
   (when-let [reg (:gmaps/bridge-registry db)]
     (let [gmaps @(:gmaps reg)
           gmaps-marker @(:gmaps-marker reg)
           gmap @(:gmap reg)
           zones (or (:zones db) {})
           bounds (:zones-bounds db)]
       (when (and gmaps gmap bounds)
         {:fx [[::sync-overlays
                {:gmaps gmaps
                 :gmaps-marker gmaps-marker
                 :gmap gmap
                 :zones zones
                 :overlays-reg reg
                 :read-only? (:gmaps/read-only? db)}]]})))))

(defn- descendant-zone-ids-explicit
  [nodes root-id]
  (loop [seen #{}
         pending [root-id]]
    (if (empty? pending)
      seen
      (let [current (first pending)
            pending (subvec pending 1)]
        (if (contains? seen current)
          (recur seen pending)
          (let [children (->> nodes
                              (keep (fn [[id n]]
                                      (when (= (:zone/parent-zone-id n) current)
                                        id)))
                              vec)]
            (recur (conj seen current)
                   (into pending children))))))))

(defn- subzone-name
  [parent-name subzone-number]
  (let [remainder (mod subzone-number 26)
        subzone-letter (js/String.fromCharCode (+ (.charCodeAt "A") remainder))
        round (quot subzone-number 26)]
    (str parent-name subzone-letter (when (pos? round) (inc round)))))

(defn- rewire-nodes
  [nodes]
  (let [base
        (medley/map-vals
         (fn [n]
           (if (:zone/id n)
             (assoc n
                    :zone/subzones #{}
                    :zone/pois #{}
                    :zone/zIndex (if (:zone/parent-zone-id n)
                                   1 0))
             n))
         (or nodes {}))
        with-subzones
        (reduce-kv
         (fn [acc zid {:zone/keys [parent-zone-id]}]
           (if parent-zone-id
             (update-in acc [parent-zone-id :zone/subzones] (fnil conj #{}) zid)
             acc))
         base
         base)
        rewired-nodes
        (reduce-kv
         (fn [acc pid {:poi/keys [id marker parent-zone-id]}]
           (if id
             (let [parent-id (or parent-zone-id
                                 (when marker (containing-zone-id acc marker)))]
               (if parent-id
                 (-> acc
                     (assoc-in [pid :poi/parent-zone-id] parent-id)
                     (update-in [parent-id :zone/pois] (fnil conj #{}) pid))
                 acc))
             acc))
         with-subzones
         with-subzones)
        renamed-nodes
        (reduce-kv
         (fn [acc node-id node]
           (cond
             ;; POIs don't have names, so we don't need to rename them
             (some? (:poi/id node)) acc
             ;; Subzones are renamed when we rename their parents, so skip them.
             (some? (:zone/parent-zone-id node)) acc
             ;; (top level) zone, so rename it (and all its subzones) accordingly.
             :else
             (let [zone-name (str (t [:common/zone]) " " (:zone-number acc))
                   subzones-to-rename (into {}
                                            (map (fn [[subzone-id idx]]
                                                   [subzone-id (subzone-name zone-name idx)]))
                                            (zipmap (:zone/subzones node)
                                                    (range (count (:zone/subzones node)))))]
               (-> (reduce
                    (fn [acc [subzone-id subzone-name]]
                      (assoc-in acc
                                [:rewired-nodes subzone-id :zone/name]
                                subzone-name))
                    acc
                    subzones-to-rename)
                   (update-in [:rewired-nodes node-id] #(assoc % :zone/name zone-name))
                   (update :zone-number inc)))))
         {:rewired-nodes rewired-nodes
          :zone-number 1}
         rewired-nodes)]
    (:rewired-nodes renamed-nodes)))

(defn next-zone-name
  [zones parent-id]
  (if-not parent-id
    (let [zone-count (count (filterv (fn [zone]
                                       (and (:zone/id zone)
                                            (nil? (:zone/parent-zone-id zone))))
                                     (vals zones)))]
      (str (t [:common/zone]) " " (inc zone-count)))
    (let [subzone-count (count (filterv
                                (fn [zone]
                                  (and (:zone/id zone)
                                       (= parent-id (:zone/parent-zone-id zone))))
                                (vals zones)))]
      (subzone-name (get-in zones [parent-id :zone/name]) subzone-count))))

(rf/reg-event-fx
 ::zone-created
 (fn [{:keys [db]} [_ {:keys [center parent-zone-id]}]]
   (let [gmap (-> db :gmaps/bridge-registry :gmap deref)
         parent-zone-id (root-parent-id (:zones db) parent-zone-id)
         zone-name (next-zone-name (:zones db) parent-zone-id)
         parent-polygon (when parent-zone-id
                          (get-in db [:zones parent-zone-id :zone/polygon]))
         polygon (if-not parent-polygon
                   (let [viewport-width (gmap-width gmap)
                         viewport-height (gmap-height gmap)]
                     (util.geometry/zone-polygon
                      {:width viewport-width
                       :height viewport-height}
                      center))
                   (util.geometry/zone-polygon parent-polygon center 0.4))]
     (if-not polygon
       {:db db}
       (let [zone-id (random-uuid)
             zone #:zone{:id zone-id
                         :name zone-name
                         :parent-zone-id parent-zone-id
                         :polygon polygon}]
         {:db (-> db
                  (assoc-in [:zones zone-id] zone)
                  (update :zones rewire-nodes))
          :fx [[:dispatch [::render-overlays]]]})))))

(rf/reg-event-fx
 ::zone-selected
 (fn [{:keys [db]} _]
   {:db db}))

(rf/reg-event-db
 ::zone-renamed
 (fn [db [_ zone-id zone-name]]
   (assoc-in db [:zones zone-id :zone/name] zone-name)))

(rf/reg-event-fx
 ::zone-deleted
 (fn [{:keys [db]} [_ zone-id]]
   (let [nodes (or (:zones db) {})
         zids-to-remove (descendant-zone-ids nodes zone-id)
         poi-ids-to-remove (descendant-poi-ids nodes zone-id)
         ids-to-remove (into zids-to-remove poi-ids-to-remove)]
     {:db (-> db
              (update :zones #(apply dissoc % ids-to-remove))
              (update :zones rewire-nodes))
      :fx [[:dispatch [::render-overlays]]]})))

(rf/reg-event-fx
 ::poi-created
 (fn [{:keys [db]} [_ {:keys [poi-type coordinates parent-zone-id work-package]}]]
   (let [{:keys [lat lng]} coordinates
         marker [lng lat]
         poi-id (random-uuid)
         poi #:poi{:id poi-id
                   :type poi-type
                   :marker marker
                   :parent-zone-id parent-zone-id
                   :work-package work-package}]
     {:db (-> db
              (assoc-in [:zones poi-id] poi)
              (update :zones rewire-nodes))
      :fx [[:dispatch [::render-overlays]]]})))

(rf/reg-event-fx
 ::poi-updated
 (fn [{:keys [db]} [_ {:keys [id poi-type coordinates parent-zone-id work-package]}]]
   (let [{:keys [lat lng]} coordinates
         marker [lng lat]
         poi #:poi{:id id
                   :type poi-type
                   :marker marker
                   :parent-zone-id parent-zone-id
                   :work-package work-package}]
     {:db (-> db
              (assoc-in [:zones id] poi)
              (update :zones rewire-nodes))
      :fx [[:dispatch [::render-overlays]]]})))

(rf/reg-event-fx
 ::poi-moved
 (fn [{:keys [db]} [_ poi-id new-marker]]
   (let [nodes (or (:zones db) {})
         old-marker (get-in nodes [poi-id :poi/marker])
         old-parent (get-in nodes [poi-id :poi/parent-zone-id])
         new-parent (containing-zone-id nodes new-marker)
         final-marker (if new-parent new-marker old-marker)
         final-parent (or new-parent old-parent)
         reg (:gmaps/bridge-registry db)
         poi-overlays* (:pois reg)
         marker-inst (get @poi-overlays* poi-id)]
     (when marker-inst
       (set-marker-position! marker-inst final-marker))
     {:db (-> db
              (assoc-in [:zones poi-id :poi/marker] final-marker)
              (assoc-in [:zones poi-id :poi/parent-zone-id] final-parent)
              (update :zones rewire-nodes))
      :fx [[:dispatch [::render-overlays]]]})))

(rf/reg-event-fx
 ::poi-selected
 (fn [{:keys [db]} _]
   {:db db}))

(rf/reg-event-fx
 ::poi-deleted
 (fn [{:keys [db]} [_ poi-id]]
   {:db (-> db
            (update :zones dissoc poi-id)
            (update :zones rewire-nodes))
    :fx [[:dispatch [::render-overlays]]]}))

(rf/reg-event-fx
 ::initialize-zones
 (fn [{:keys [db]} [_ data]]
   (let [points (->> data
                     (mapcat (some-fn
                              :zone/polygon
                              #(when-let [m (:poi/marker %)] [m]))))
         bounds {:east  (apply max (map first points))
                 :north (apply max (map second points))
                 :south (apply min (map second points))
                 :west  (apply min (map first points))}]
     {:db (-> db
              (assoc :zones (->> data (medley/index-by (some-fn :zone/id :poi/id))))
              (cond-> bounds (assoc :zones-bounds bounds))
              (update :zones rewire-nodes))
      :fx [[:dispatch [::render-overlays]]]})))

(rf/reg-event-db
 ::start-placing
 (fn [db [_ tool]]
   (-> db
       (dissoc :gmaps/options-menu)
       (assoc :gmaps/placing-tool tool))))

(rf/reg-event-db
 ::stop-placing
 (fn [db _]
   (-> db
       (dissoc :gmaps/options-menu)
       (dissoc :gmaps/placing-tool))))

(rf/reg-sub
 ::placing-tool
 (fn [db _]
   (:gmaps/placing-tool db)))

(rf/reg-event-fx
 ::map-clicked
 (fn [{:keys [db]} [_ coords]]
   (case (:gmaps/placing-tool db)
     :zone
     {:db (dissoc db :gmaps/placing-tool)
      :fx [[:dispatch [::zone-created {:center coords}]]]}

     {})))

(defn- recompute-zone-parent
  [nodes zone-id new-polygon]
  (let [descendants (descendant-zone-ids-explicit nodes zone-id)
        center (util.geometry/polygon-center new-polygon)
        candidate (containing-zone-id (apply dissoc nodes (conj descendants zone-id))
                                      center)
        parent-id (root-parent-id nodes candidate)]
    {:parent-id parent-id
     :polygon new-polygon}))

(rf/reg-event-fx
  ::zone-changed
  (fn [{:keys [db]} [_ zone-id new-polygon]]
    (let [nodes (or (:zones db) {})
          {:keys [parent-id polygon]}
          (recompute-zone-parent nodes zone-id new-polygon)]
      {:db (-> db
               (assoc-in [:zones zone-id :zone/polygon] polygon)
               (assoc-in [:zones zone-id :zone/parent-zone-id] parent-id)
               (update :zones rewire-nodes))
       :fx [[:dispatch [::render-overlays]]]})))

(rf/reg-event-fx
 ::zone-drag-ended
 (fn [{:keys [db]} [_ zone-id old-polygon new-polygon]]
   (let [nodes (or (:zones db) {})
         delta (util.geometry/delta-from-first-point old-polygon new-polygon)
         original-parent-id (get-in db [:gmaps :drag :original-parent-id])
         original-parent-poly (when original-parent-id
                                (get-in nodes [original-parent-id :zone/polygon]))
         center-inside-original?
         (when original-parent-poly
           (util.geometry/zone-center-inside? original-parent-poly new-polygon))
         candidate-parent-id (let [center (util.geometry/polygon-center new-polygon)]
                               (containing-zone-id (dissoc nodes zone-id) center))
         descendants (descendant-zone-ids-explicit nodes zone-id)
         candidate-parent-id (when (and candidate-parent-id
                                        (not (contains? descendants candidate-parent-id)))
                               candidate-parent-id)
         moved-zone-ids (disj (descendant-zone-ids-explicit nodes zone-id) zone-id)
         moved-zone-set (conj moved-zone-ids zone-id)
         moved-poi-ids (->> nodes
                            (keep (fn [[id n]]
                                    (when-let [pz (:poi/parent-zone-id n)]
                                      (when (contains? moved-zone-set pz)
                                        id))))
                            set)
         final-parent-id
         (root-parent-id nodes
                         (cond
                           (and original-parent-id
                                (false? center-inside-original?))
                           nil

                           candidate-parent-id
                           candidate-parent-id

                           :else original-parent-id))
         final-parent-poly (when final-parent-id
                             (get-in nodes [final-parent-id :zone/polygon]))
         final-polygon (if final-parent-poly
                         (util.geometry/snap-child-into-parent final-parent-poly new-polygon)
                         new-polygon)
         reg (:gmaps/bridge-registry db)
         zone-overlays* (:zones reg)
         poi-overlays* (:pois reg)
         live-polys (into {}
                          (map (fn [zid]
                                 [zid (if-let [poly (get @zone-overlays* zid)]
                                        (polygon-path poly)
                                        (get-in db [:zones zid :zone/polygon]))]))
                          (conj moved-zone-ids zone-id))
         db' (-> db
                 (assoc-in [:zones zone-id :zone/polygon] final-polygon)
                 (assoc-in [:zones zone-id :zone/parent-zone-id] final-parent-id)
                 (cond-> delta
                   (as-> dbx
                         (reduce (fn [acc zid]
                                   (assoc-in acc [:zones zid :zone/polygon]
                                             (util.geometry/translate-polygon (get live-polys zid) delta)))
                                 dbx
                                 (conj moved-zone-ids zone-id))))
                 (update :zones rewire-nodes))
         moved? (not= [0 0] delta)]
     (when moved?
       (doseq [zid moved-zone-ids]
         (when-let [poly (and zone-overlays* (get @zone-overlays* zid))]
           (when-let [p (get-in db' [:zones zid :zone/polygon])]
             (set-polygon-path! poly p))))
       (doseq [pid moved-poi-ids]
         (when-let [m (and poi-overlays* (get @poi-overlays* pid))]
           (when-let [p (get-in db' [:zones pid :poi/marker])]
             (set-marker-position! m p)))))
     {:db db'
      :fx [[:dispatch [::render-overlays]]]})))

(rf/reg-event-db
 ::zone-drag-started
 (fn [db [_ {:keys [zone-id old-polygon]}]]
   (let [original-parent-id (get-in db [:zones zone-id :zone/parent-zone-id])]
     (-> db
         (assoc-in [:gmaps :drag :zone-id] zone-id)
         (assoc-in [:gmaps :drag :old-polygon] old-polygon)
         (assoc-in [:gmaps :drag :original-parent-id] original-parent-id)))))

(defn- zone-label
  [{:zone/keys [id name polygon]}]
  (let [[lng lat] (first polygon)]
    ^{:key (str "label-" id)}
    [:> AdvancedMarker
     {:position #js {:lat lat :lng lng}
      :gmpClickable false
      :gmpDraggable false}
     [:div.zone-label name]]))

(defn zone-labels
  []
  (let [zones (rf/subscribe [::zones])]
    (fn []
      [:<>
       (doall
         (for [[_ node] @zones
               :when (:zone/id node)]
           ^{:key (:zone/id node)}
           [zone-label node]))])))
